import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { PointsToMxnSetting } from "../interface/points-config";
import {
  POINTS_TO_MXN_KEY,
  LOGO_LIGHT_KEY,
  LOGO_DARK_KEY,
  LOGO_ICON_KEY,
  DEVICE_MOCKUP_IMAGE_KEY,
  APP_STORE_URL_KEY,
  GOOGLE_PLAY_URL_KEY,
} from "../constants/points";

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/settings`;

  private readonly logoLightSubject = new BehaviorSubject<string | null>(null);
  private readonly logoDarkSubject = new BehaviorSubject<string | null>(null);
  private readonly logoIconSubject = new BehaviorSubject<string | null>(null);
  private readonly deviceMockupImageSubject = new BehaviorSubject<string | null>(null);
  private readonly appStoreUrlSubject = new BehaviorSubject<string | null>(null);
  private readonly googlePlayUrlSubject = new BehaviorSubject<string | null>(null);

  readonly logoLight$ = this.logoLightSubject.asObservable();
  readonly logoDark$ = this.logoDarkSubject.asObservable();
  readonly logoIcon$ = this.logoIconSubject.asObservable();
  readonly deviceMockupImage$ = this.deviceMockupImageSubject.asObservable();
  readonly appStoreUrl$ = this.appStoreUrlSubject.asObservable();
  readonly googlePlayUrl$ = this.googlePlayUrlSubject.asObservable();

  constructor() {
    this.loadLogos();
  }

  private loadLogos(): void {
    this.getByKey<string>(LOGO_LIGHT_KEY).subscribe({
      next: (url) => this.logoLightSubject.next(url),
      error: () => {},
    });
    this.getByKey<string>(LOGO_DARK_KEY).subscribe({
      next: (url) => this.logoDarkSubject.next(url),
      error: () => {},
    });
    this.getByKey<string>(LOGO_ICON_KEY).subscribe({
      next: (url) => this.logoIconSubject.next(url),
      error: () => {},
    });
    this.getByKey<string>(DEVICE_MOCKUP_IMAGE_KEY).subscribe({
      next: (url) => this.deviceMockupImageSubject.next(url),
      error: () => {},
    });
    this.getByKey<string>(APP_STORE_URL_KEY).subscribe({
      next: (url) => this.appStoreUrlSubject.next(url),
      error: () => {},
    });
    this.getByKey<string>(GOOGLE_PLAY_URL_KEY).subscribe({
      next: (url) => this.googlePlayUrlSubject.next(url),
      error: () => {},
    });
  }

  getByKey<T>(key: string): Observable<T | null> {
    return this.http
      .get<{ data?: { items?: T } }>(`${this.baseUrl}/${key}`)
      .pipe(map((res) => (res?.data && 'items' in res.data ? res.data.items : null) ?? null));
  }

  setByKey(key: string, value: unknown): Observable<unknown> {
    return this.http
      .put<{ data?: { items?: unknown } }>(`${this.baseUrl}/${key}`, { value })
      .pipe(map((res) => (res?.data && 'items' in res.data ? res.data.items : null) ?? null));
  }

  uploadImage(file: File): Observable<string> {
    const formData = new FormData();
    formData.append("file", file);
    return this.http
      .post<{ data?: { url?: string } }>(`${this.baseUrl}/upload-image`, formData)
      .pipe(map((res) => res?.data?.url ?? ""));
  }

  getPointsToMxn(): Observable<PointsToMxnSetting | null> {
    return this.getByKey<PointsToMxnSetting>(POINTS_TO_MXN_KEY);
  }

  updatePointsToMxn(points: number, mxn: number): Observable<PointsToMxnSetting | null> {
    return this.setByKey(POINTS_TO_MXN_KEY, { points, mxn }).pipe(
      map((data) => data as PointsToMxnSetting | null)
    );
  }

  setLogoLight(url: string): Observable<unknown> {
    return this.setByKey(LOGO_LIGHT_KEY, url).pipe(
      tap(() => this.logoLightSubject.next(url))
    );
  }

  setLogoDark(url: string): Observable<unknown> {
    return this.setByKey(LOGO_DARK_KEY, url).pipe(
      tap(() => this.logoDarkSubject.next(url))
    );
  }

  setLogoIcon(url: string): Observable<unknown> {
    return this.setByKey(LOGO_ICON_KEY, url).pipe(
      tap(() => this.logoIconSubject.next(url))
    );
  }

  setDeviceMockupImage(url: string): Observable<unknown> {
    return this.setByKey(DEVICE_MOCKUP_IMAGE_KEY, url).pipe(
      tap(() => this.deviceMockupImageSubject.next(url))
    );
  }

  setAppStoreUrl(url: string): Observable<unknown> {
    return this.setByKey(APP_STORE_URL_KEY, url).pipe(
      tap(() => this.appStoreUrlSubject.next(url))
    );
  }

  getCancellationAlertPhones(): Observable<string[]> {
    return this.getByKey<string[]>("branch_cancellation_alert_phones").pipe(
      map((value) => (Array.isArray(value) ? value : []))
    );
  }

  setCancellationAlertPhones(phones: string[]): Observable<unknown> {
    return this.setByKey("branch_cancellation_alert_phones", phones);
  }

  getCancellationApologyPoints(): Observable<number | null> {
    return this.getByKey<number>("branch_cancellation_apology_points");
  }

  setCancellationApologyPoints(points: number): Observable<unknown> {
    return this.setByKey("branch_cancellation_apology_points", points);
  }

  setGooglePlayUrl(url: string): Observable<unknown> {
    return this.setByKey(GOOGLE_PLAY_URL_KEY, url).pipe(
      tap(() => this.googlePlayUrlSubject.next(url))
    );
  }
}
